import type { IRecordType, NRecords } from '@app/types';

import type { CalendarEvent, Employee } from '../data';

export const addClientGroupIdsToEvents = (
  events: CalendarEvent[]
): CalendarEvent[] => {
  const eventsByClientId = new Map<number, CalendarEvent[]>();
  let nextGroupId = 1;
  const groupIdsByClientId = new Map<number, number>();

  for (const event of events) {
    const clientId = event.rawRecord.ClientId;

    if (!clientId) {
      continue;
    }

    const currentClientEvents = eventsByClientId.get(clientId) ?? [];
    currentClientEvents.push(event);
    eventsByClientId.set(clientId, currentClientEvents);
  }

  for (const [clientId, clientEvents] of eventsByClientId) {
    if (clientEvents.length < 2) {
      continue;
    }

    groupIdsByClientId.set(clientId, nextGroupId);
    nextGroupId += 1;
  }

  return events.map((event) => {
    const clientId = event.rawRecord.ClientId;

    if (!clientId) {
      return event;
    }

    const clientGroupId = groupIdsByClientId.get(clientId);

    if (!clientGroupId) {
      return event;
    }

    return {
      ...event,
      clientGroupId,
    };
  });
};

export const extractTime = (dateTimeString: string): string => {
  const dateObj = new Date(dateTimeString);

  if (!isNaN(dateObj.getTime())) {
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  const timePart = dateTimeString.split('T')[1] ?? dateTimeString;

  return timePart.slice(0, 5);
};

export const mapEmployeeRecordsToEmployee = (
  record: NRecords.ModelEmployeeRecords
): Employee => {
  const nameParts = [record.NameLast, record.NameFirst, record.NameMiddle]
    .filter(Boolean)
    .join(' ');

  const workSchedule = record.WorkSchedules?.[0];
  const hasActiveWorkday = Boolean(workSchedule) && !record.IsDeleted;
  const workStart = workSchedule ? extractTime(workSchedule.TimeS) : '09:00';
  const workEnd = workSchedule ? extractTime(workSchedule.TimeE) : '18:00';

  return {
    id: String(record.UserId),
    name: nameParts || record.NameFull || '',
    firstName: record.NameFirst ?? undefined,
    lastName: record.NameLast ?? undefined,
    position: record.Position ?? undefined,
    avatarUrl: record.Photo ?? undefined,
    hasActiveWorkday,
    isWorkdayDeleted: record.IsDeleted,
    workStart,
    workEnd,
    breaks: hasActiveWorkday
      ? (record.WorkBreaks ?? []).map((wb) => ({
          id: wb.Id,
          timeStart: extractTime(wb.TimeS),
          timeEnd: extractTime(wb.TimeE),
        }))
      : [],
  };
};

export const mapRecordToCalendarEvent = (
  record: IRecordType
): CalendarEvent => {
  const clientName = [record.NameLast, record.NameFirst]
    .filter(Boolean)
    .join(' ');

  const services =
    record.ClientServices?.filter((service) => Boolean(service.Name)).map(
      (service) => service.Name!
    ) ?? [];

  return {
    id: String(record.Id),
    employeeId: String(record.EmployeeId),
    title: services[0] ?? '',
    start: extractTime(record.TimeS),
    end: extractTime(record.TimeE),
    clientName: clientName || undefined,
    phone: record.Phone || undefined,
    services: services.length > 0 ? services : undefined,
    rawRecord: record,
  };
};

export const mapEmployeesRecordsResponse = (
  data: NRecords.ModelEmployeeRecords[]
): { employees: Employee[]; events: CalendarEvent[] } => {
  const employees: Employee[] = [];
  const events: CalendarEvent[] = [];

  for (const record of data) {
    const visibleRecords = (record.Records ?? []).filter(
      (entry) => !entry.IsDeleted
    );
    const hasActiveWorkday =
      Boolean(record.WorkSchedules?.[0]) && !record.IsDeleted;
    const hasAppointments = visibleRecords.length > 0;

    if (!hasActiveWorkday && !hasAppointments) {
      continue;
    }

    employees.push(mapEmployeeRecordsToEmployee(record));

    for (const entry of visibleRecords) {
      events.push(mapRecordToCalendarEvent(entry));
    }
  }

  return {
    employees,
    events: addClientGroupIdsToEvents(events),
  };
};
